import React, {Component, forwardRef} from 'react'
import './DirectBilling.css'
import {Col, Container, Dropdown, Row} from "react-bootstrap";
import PageTitle from "../../../../../layout/PageTitle";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import DateUtil from "../../../../../utils/DateUtil";
import moment from "moment";
import PrimeDateRangeInput from "../../../../../components/date-input/PrimeDateRangeInput";
import AppointmentSelectionModal from "../AppointmentSelectionModal";
import {Modal} from "../../../../../components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import EntityNoteListModal from "../notes/EntityNoteListModal";
import dialog from "../../../../../components/dialog/Dialog";
import confirmDialog from "../../../../../components/dialog/ConfirmDialog";
import ConsentFormCode from "../../../../consentform/ConsentFormCode";
import {downloadBlob} from "../../../../../utils/FileDownloader";
import ReferralCycleEnum from "../../../enums/ReferralCycleEnum";
import InsuranceReferralStatus from "../../../enums/InsuranceReferralStatus";
import HoverableDescriptionBox from "../../../../../components/hoverable-desc-box/HoverableDescriptionBox";
import Select from "react-select";
import DirectBillingPatientInformedStatusEnum from "../../../enums/DirectBillingPatientInformedStatusEnum";
import EnumDirectBillingType from "../../direct/EnumDirectBillingType";

export default class DirectBilling extends Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            dateRange: [new Date(), new Date()],
            errorMessages: [],
            showAppointmentModal: false,
            selectedRowData: null,
            activeNotesModalId: null,
            page: 1,
            rows: 10,
            totalCount: 0,
            filters: {
                status: '',
                patientName: '',
                patientMrn: '',
                referenceNo: '',
                cycle: '',
            }
        }
    }

    componentDidMount() {
        if (this.props.selected) {
            this.retrieveItems();
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!prevProps.selected && this.props.selected) {
            this.retrieveItems();
        }
    }

    componentWillUnmount() {
        if (this.filterDebounce) {
            clearTimeout(this.filterDebounce);
        }
    }

    handleDateRangeChange = (dateRange) => {
        this.setState({ dateRange, page: 1 }, () => {
            if (dateRange && dateRange[0] && dateRange[1]) {
                this.retrieveItems();
            }
        });
    }

    retrieveItems() {
        const { dateRange, page, rows, filters } = this.state;
        const [startDate, endDate] = dateRange || [new Date(), new Date()];

        const params = {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            page,
            size: rows,
        };
        // Only send non-empty filters so they combine with AND on the backend.
        if (filters.cycle) params.cycle = filters.cycle;
        if (filters.status) params.status = filters.status;
        if (filters.referenceNo) params.referenceNo = filters.referenceNo;
        if (filters.patientMrn) params.mrn = filters.patientMrn;
        if (filters.patientName) params.name = filters.patientName;

        RemotingService.getRemoteCall(
            'api/insurance/coordinator/v2/direct-billing',
            params,
            (result) => {
                const items = result?.items || [];
                this.setState({
                    items,
                    totalCount: result?.totalCount || 0,
                    page: result?.currentPage || page,
                });
            },
            (error) => {
                this.setState({errorMessages: [error.message]});
            }
        );
    }

    // Text filters fire on every keystroke; debounce the backend fetch so we
    // don't send a request per character.
    debouncedRetrieve = () => {
        if (this.filterDebounce) {
            clearTimeout(this.filterDebounce);
        }
        this.filterDebounce = setTimeout(() => {
            this.retrieveItems();
        }, 400);
    }

    handleFilterChange = (field, value) => {
        const isTextFilter = field === 'patientName' || field === 'patientMrn' || field === 'referenceNo';
        this.setState({
            filters: { ...this.state.filters, [field]: value },
            page: 1
        }, () => {
            if (isTextFilter) {
                this.debouncedRetrieve();
            } else {
                this.retrieveItems();
            }
        });
    }

    statusTemplate = (rowData) => {
        const { status } = rowData;
        if (!status) return '-';
        const label = InsuranceReferralStatus[status]?.name || status;
        return <div style={{textAlign: 'center', fontSize: '12px'}}>{label}</div>;
    }

    clinicTemplate = (rowData) => {
        return <div style={{fontSize: '12px', textAlign: 'center'}}>{rowData.clinic?.name || '-'}</div>;
    }

    cycleTemplate = (rowData) => {
        if (!rowData.cycle) return '-';
        const label = ReferralCycleEnum[rowData.cycle]?.name || rowData.cycle;
        const detail = rowData.cycle === 'OTHER' && rowData.cycleOtherDetail ? ` (${rowData.cycleOtherDetail})` : '';
        return <div style={{fontSize: '12px', textAlign: 'center'}}>{label}{detail}</div>;
    }

    icdTemplate = (rowData) => {
        return (
            <div className="icd-cell">
                {rowData.diagnosisGroup?.diagnosisItems?.map((item, index) => {
                    const prefix = item.level === 'PRINCIPAL' ? 'P: ' : 'S: ';
                    return (
                        <HoverableDescriptionBox
                            key={`dg-${index}`}
                            header={`${prefix}${item.code}`}
                            description={item.description || item.code}
                            className="icd-box"
                        />
                    );
                })}
            </div>
        );
    }

    sessionsTemplate = (rowData, field) => {
        const items = rowData.items;
        if (!items || items.length === 0) return <span style={{fontSize: '12px'}}>-</span>;
        return (
            <div style={{fontSize: '12px'}}>
                {items.map((item, i) => (
                    <div key={i} style={{whiteSpace: 'nowrap', marginBottom: '2px'}}>
                        <b>{item.cpt?.cptCode}:</b> {item[field] ?? 0}/{item.noOfApprovedSessions ?? 0}
                    </div>
                ))}
            </div>
        );
    }

    patientNameTemplate = (rowData) => {
        const p = rowData.patient;
        if (!p) return '-';
        return (
            <div className="patient-name" style={{fontSize: '14px', textAlign: 'center'}}>
                {p.firstName + (p.middleName ? ' ' + p.middleName + ' ' : ' ') + p.lastName}
            </div>
        );
    }

    mrnTemplate = (rowData) => {
        return <div style={{fontSize: '13px', textAlign: 'center'}}>{rowData.patient?.fullMrn || '-'}</div>;
    }

    insuranceTemplate = (rowData) => {
        const ins = rowData.patient?.insurance;
        if (!ins) return '-';
        return (
            <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                <div><b>{ins.tpa?.name}</b> / {ins.company?.name}</div>
                <div style={{color: '#555'}}>{ins.memberId || ''}</div>
            </div>
        );
    }

    referenceNoTemplate = (rowData) => {
        return <span style={{fontSize: '12px'}}>{rowData.referenceNo || '-'}</span>;
    }

    referredByTemplate = (rowData) => {
        return <span style={{fontSize: '12px'}}>{rowData.referrerName || '-'}</span>;
    }

    validUntilTemplate = (rowData) => {
        return <span style={{fontSize: '12px'}}>{rowData.validUntil ? DateUtil.formatDate(rowData.validUntil, 'Do MMM YYYY') : '-'}</span>;
    }

    notesTemplate = (rowData) => {
        const notes = rowData.notes || [];
        const count = notes.filter(n => n.isActive).length;
        return (
            <div style={{display: 'flex', justifyContent: 'center'}}>
                <button
                    className="btn btn-link btn-sm p-0"
                    style={{fontSize: '13px'}}
                    onClick={() => this.setState({activeNotesModalId: rowData.id})}
                >
                    <FontAwesomeIcon icon={["fas", "sticky-note"]} className="mr-1" />
                    Notes ({count})
                </button>
            </div>
        );
    }

    // Template for displaying co-pay information
    coPayTemplate = (rowData) => {
        const coPayMax = rowData.coPayMax;
        
        if (coPayMax) {
            return coPayMax.toString();
        } else {
            return '-';
        }
    }

    // Template for displaying co-pay unit
    coPayUnitTemplate = (rowData) => {
        const coPayUnit = rowData.coPayUnit;
        return coPayUnit || '-';
    }

    // Template for displaying session numbers
    sessionTemplate = (rowData, field) => {
        const value = rowData[field];
        return value !== null && value !== undefined ? value.toString() : '-';
    }

    // Template for displaying creation date
    creationDateTemplate = (rowData) => {
        if (rowData.creationDate) {
            return DateUtil.formatDate(rowData.creationDate, "Do MMMM YYYY / hh:mm A");
        } else {
            return '-';
        }
    }

    patientInformedStatusTemplate = (rowData) => {
        const opts = DirectBillingPatientInformedStatusEnum.toArray().map(i => ({ value: i.key, label: i.name }));
        const selectedOption = opts.find(o => o.value === rowData.patientInformedStatus) || null;
        return (
            <div style={{width: '100%'}}>
                <Select
                    options={opts}
                    value={selectedOption}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(opt) => {
                        const value = opt ? opt.value : null;
                        RemotingService.putRemoteCall(
                            `api/insurance/coordinator/v2/direct-billing/${rowData.id}`,
                            { patientInformedStatus: value },
                            () => {
                                this.setState(prev => ({
                                    items: prev.items.map(i => i.id === rowData.id ? { ...i, patientInformedStatus: value } : i)
                                }));
                            }
                        );
                    }}
                    styles={{
                        container: (b) => ({ ...b, width: '100%' }),
                        control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px', width: '100%' }),
                        valueContainer: (b) => ({ ...b, height: '28px', padding: '0 4px' }),
                        input: (b) => ({ ...b, margin: 0, padding: 0 }),
                        indicatorsContainer: (b) => ({ ...b, height: '28px' }),
                        dropdownIndicator: (b) => ({ ...b, padding: '0 2px' }),
                        clearIndicator: (b) => ({ ...b, padding: '0 2px' }),
                        option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }),
                    }}
                />
            </div>
        );
    }

    actionsTemplate = (rowData) => (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <Dropdown>
                <Dropdown.Toggle as={DotsToggle} />
                <Dropdown.Menu style={{fontSize: '14px', minWidth: '170px', padding: '2px 0'}}>
                    <Dropdown.Item onClick={() => this.handleAdditionalSessions(rowData)} disabled={rowData.copiedToId != null} style={{padding: '4px 10px'}}>
                        <FontAwesomeIcon icon={["fas", "plus-circle"]} className="mr-1" /> Additional Sessions
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => this.handleExtendValidity(rowData)} disabled={rowData.copiedToId != null} style={{padding: '4px 10px'}}>
                        <FontAwesomeIcon icon={["fas", "calendar-alt"]} className="mr-1" /> Extend Validity
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => this.handleAddAppointment(rowData)} style={{padding: '4px 10px'}}>
                        <FontAwesomeIcon icon={["fas", "calendar-plus"]} className="mr-1" /> Add Appointment
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => this.handleEditForm(rowData)} style={{padding: '4px 10px'}}>
                        <FontAwesomeIcon icon={["fas", "file-signature"]} className="mr-1" /> Edit Form
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => this.handleDownloadForm(rowData)} style={{padding: '4px 10px'}}>
                        <FontAwesomeIcon icon={["fas", "download"]} className="mr-1" /> Download Form
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )

    handleAdditionalSessions = (rowData) => {
        confirmDialog(
            'Additional Sessions',
            'Are you sure to request additional sessions for this direct billing?',
            () => this.performCopy('ADDITIONAL_SESSIONS', rowData)
        );
    }

    handleExtendValidity = (rowData) => {
        confirmDialog(
            'Extend Validity',
            'Are you sure to request validity extension for this direct billing?',
            () => this.performCopy('VALIDITY_EXTENSION', rowData)
        );
    }

    // Copy the direct billing with 0 sessions per CPT for the given operation type.
    performCopy = (type, rowData) => {
        const sessionsPerCpt = {};
        (rowData?.items || []).forEach(item => {
            const cptCode = item.cpt?.cptCode;
            if (cptCode) sessionsPerCpt[cptCode] = 0;
        });
        RemotingService.postRemoteCall(
            `api/insurance/coordinator/v2/direct-billing/${rowData.id}/copy`,
            { type, sessionsPerCpt },
            () => this.retrieveItems(),
            (error) => this.setState({ errorMessages: [error.message || 'Failed to copy direct billing'] })
        );
    }

    // Generate a 6-digit code so the patient can sign the attendance form on a
    // tablet via the public /forms page, and show it in a dialog.
    handleEditForm = (rowData) => {
        if (!rowData || !rowData.id || !rowData.patient?.id) {
            this.setState({ errorMessages: ['Cannot generate a code: missing patient or record reference.'] });
            return;
        }
        RemotingService.postRemoteCall(
            'api/consent-form/code',
            {
                formType: 'DIRECT_BILLING_ATTENDANCE_FORM',
                patientId: rowData.patient.id,
                directBillingId: rowData.id,
            },
            (response) => {
                dialog({
                    style: { width: 450, height: 150 },
                    component: <ConsentFormCode
                        code={response.code}
                        duration={response.durationInMinutes}
                        formName="Direct Billing Attendance Form"
                    />,
                    disableBackdropClick: false,
                });
            },
            (error) => {
                this.setState({ errorMessages: [error.message || 'Failed to generate code'] });
            }
        );
    }

    // Download the attendance-form PDF: generate the report (returns a File with a
    // uuid), then download it through the standard file-download endpoint.
    handleDownloadForm = (rowData) => {
        if (!rowData || !rowData.id) {
            this.setState({ errorMessages: ['Cannot download: missing record reference.'] });
            return;
        }
        RemotingService.getRemoteCall(
            `api/insurance/coordinator/v2/direct-billing/${rowData.id}/attendance-form/report`,
            null,
            (file) => {
                if (!file || !file.uuid) {
                    this.setState({ errorMessages: ['Failed to generate the form PDF.'] });
                    return;
                }
                RemotingService.instance.get(`api/file/download/${file.uuid}`, { responseType: 'blob' })
                    .then((response) => {
                        downloadBlob(
                            new Blob([response.data], { type: file.contentType || 'application/pdf' }),
                            file.name
                        );
                    })
                    .catch(() => {
                        this.setState({ errorMessages: ['Failed to download the form PDF.'] });
                    });
            },
            (error) => {
                this.setState({ errorMessages: [error.message || 'Failed to generate the form PDF.'] });
            }
        );
    }

    handleAddAppointment = (rowData) => {
        this.setState({
            showAppointmentModal: true,
            selectedRowData: rowData
        });
    }

    handleCloseAppointmentModal = () => {
        this.setState({
            showAppointmentModal: false,
            selectedRowData: null
        });
    }

    handleAppointmentSelected = (appointment) => {
        const { selectedRowData } = this.state;
        
        if (!selectedRowData || !appointment) {
            this.setState({errorMessages: ['Invalid selection data']});
            return;
        }

        RemotingService.postRemoteCall(
            `api/insurance/coordinator/v2/direct-billing/${selectedRowData.id}/appointment/${appointment.id}`,
            null,
            (response) => {
                console.log('Appointment linked successfully:', response);
                this.retrieveItems();
            },
            (error) => {
                this.setState({errorMessages: [error.message || 'Failed to link appointment']});
            }
        );
    }

    render() {
        const { errorMessages, page, rows, totalCount } = this.state;
        const first = (page - 1) * rows;

        return (
            <Container style={{maxWidth: '100%'}}>
                <Row>
                    <Col xs={12}>
                        <div style={{padding: '10px'}} className="data-table-header-wrapper">
                            <div className="float-left">
                                <PageTitle title="Direct Billing" className="bg-white"/>
                            </div>
                            <div className="float-right" style={{display: "flex", alignItems: "center", gap: "15px"}}>
                                <PrimeDateRangeInput
                                    value={this.state.dateRange}
                                    onChange={this.handleDateRangeChange}
                                />
                            </div>
                        </div>
                        
                        {errorMessages.length > 0 && (
                            <div className="alert alert-danger">
                                {errorMessages.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </div>
                        )}

                        <DataTable value={this.state.items}
                                  responsiveLayout="scroll"
                                  paginator
                                  lazy
                                  first={first}
                                  totalRecords={totalCount}
                                  onPage={(event) => {
                                      const newPage = (event.page ?? 0) + 1; // PrimeReact page is 0-based; API expects 1-based
                                      this.setState({ page: newPage, rows: event.rows }, () => {
                                          this.retrieveItems();
                                      });
                                  }}
                                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                  rows={rows}
                                  rowsPerPageOptions={[10, 25, 50]}
                                  className="p-datatable-sm"
                                  rowClassName={(rowData) => {
                                      const { status } = rowData;
                                      return {
                                          'row-status-approved': status === 'APPROVED',
                                          'row-status-requested': status === 'REQUESTED' || status === 'EDIT',
                                          'row-status-completed': status === 'COMPLETED' || status === 'EXPIRED',
                                          'row-status-denied': status === 'DENIED' || status === 'CANCELLED',
                                      };
                                  }}>
                            <Column header="Clinic"
                                    body={this.clinicTemplate}
                                    headerStyle={{textAlign: 'center'}}
                                    style={{textAlign: 'center'}}/>
                            <Column field="cycle"
                                    header="Cycle"
                                    body={this.cycleTemplate}
                                    headerStyle={{textAlign: 'center'}}
                                    style={{textAlign: 'center'}}
                                    filter filterElement={
                                        <Select isClearable
                                            options={ReferralCycleEnum.toArray().map(i => ({ value: i.key, label: i.name }))}
                                            value={this.state.filters.cycle ? { value: this.state.filters.cycle, label: ReferralCycleEnum[this.state.filters.cycle]?.name } : null}
                                            onChange={(opt) => this.handleFilterChange('cycle', opt ? opt.value : '')}
                                            menuPortalTarget={document.body} menuPosition="fixed"
                                            styles={{ control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px' }), valueContainer: (b) => ({ ...b, height: '28px', padding: '0 6px' }), input: (b) => ({ ...b, margin: 0, padding: 0 }), indicatorsContainer: (b) => ({ ...b, height: '28px' }), option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }) }}
                                        />
                                    }/>
                            <Column header="Status"
                                    body={this.statusTemplate}
                                    headerStyle={{textAlign: 'center'}}
                                    style={{textAlign: 'center'}}
                                    filter filterElement={
                                        <Select isClearable
                                            options={InsuranceReferralStatus.toArray().map(i => ({ value: i.key, label: i.name }))}
                                            value={this.state.filters.status ? { value: this.state.filters.status, label: InsuranceReferralStatus[this.state.filters.status]?.name } : null}
                                            onChange={(opt) => this.handleFilterChange('status', opt ? opt.value : '')}
                                            menuPortalTarget={document.body} menuPosition="fixed"
                                            styles={{ control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px' }), valueContainer: (b) => ({ ...b, height: '28px', padding: '0 6px' }), input: (b) => ({ ...b, margin: 0, padding: 0 }), indicatorsContainer: (b) => ({ ...b, height: '28px' }), option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }) }}
                                        />
                                    }/>
                            <Column header="ICD Codes"
                                    body={this.icdTemplate}/>
                            <Column header="Used Sessions"
                                    body={(rowData) => this.sessionsTemplate(rowData, 'noOfUsedSessions')}/>
                            <Column header="Attended Sessions"
                                    body={(rowData) => this.sessionsTemplate(rowData, 'noOfAttendedSessions')}/>
                            <Column header="Insurance"
                                    body={this.insuranceTemplate}/>
                            <Column header="Patient Name"
                                    body={this.patientNameTemplate}
                                    headerStyle={{textAlign: 'center'}}
                                    style={{textAlign: 'center'}}
                                    filter filterElement={
                                        <input type="text" style={{ width: '100%', fontSize: '11px', padding: '2px 6px', height: '28px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                            value={this.state.filters.patientName}
                                            onChange={(e) => this.handleFilterChange('patientName', e.target.value)}
                                            placeholder="Patient Name" />
                                    }/>
                            <Column header="MRN"
                                    body={this.mrnTemplate}
                                    headerStyle={{textAlign: 'center'}}
                                    style={{textAlign: 'center'}}
                                    filter filterElement={
                                        <input type="text" style={{ width: '100%', fontSize: '11px', padding: '2px 6px', height: '28px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                            value={this.state.filters.patientMrn}
                                            onChange={(e) => this.handleFilterChange('patientMrn', e.target.value)}
                                            placeholder="MRN" />
                                    }/>
                            <Column header="Ref No"
                                    body={this.referenceNoTemplate}
                                    filter filterElement={
                                        <input type="text" style={{ width: '100%', fontSize: '11px', padding: '2px 6px', height: '28px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                            value={this.state.filters.referenceNo}
                                            onChange={(e) => this.handleFilterChange('referenceNo', e.target.value)}
                                            placeholder="Ref No" />
                                    }/>
                            <Column header="Referred By"
                                    body={this.referredByTemplate}/>
                            <Column header="Valid Until"
                                    body={this.validUntilTemplate}/>
                            <Column header="Notes"
                                    body={this.notesTemplate}
                                    style={{width: '110px', textAlign: 'center'}}/>
                            <Column header="Patient Informed"
                                    body={this.patientInformedStatusTemplate}
                                    style={{width: '160px'}}/>
                            <Column header="Actions"
                                    body={this.actionsTemplate}
                                    style={{width: '50px', textAlign: 'center'}}/>
                        </DataTable>
                    </Col>
                </Row>
                
                {this.state.activeNotesModalId &&
                    <Modal
                        visible
                        header="Notes"
                        closeAction={() => this.setState({activeNotesModalId: null})}
                        contentStyle={{minWidth: 600, minHeight: 400}}
                    >
                        <EntityNoteListModal
                            entityId={this.state.activeNotesModalId}
                            baseUrl="api/insurance/coordinator/v2/direct-billing"
                            readOnly={false}
                            onNotesChange={() => this.retrieveItems()}
                        />
                    </Modal>
                }

                {/* Appointment Selection Modal */}
                <AppointmentSelectionModal
                    show={this.state.showAppointmentModal}
                    patientId={this.state.selectedRowData?.patient?.id}
                    speciality={this.state.selectedRowData?.speciality ? EnumDirectBillingType[this.state.selectedRowData.speciality]?.name : null}
                    onClose={this.handleCloseAppointmentModal}
                    onAppointmentSelected={this.handleAppointmentSelected}
                />
            </Container>
        );
    }
}

const DotsToggle = forwardRef(({ onClick }, ref) => (
    <span
        ref={ref}
        onClick={(e) => { e.preventDefault(); onClick(e); }}
        style={{ cursor: 'pointer', color: '#6c757d', fontSize: '16px', padding: '0 4px' }}
    >
        <FontAwesomeIcon icon={["fas", "ellipsis-v"]} />
    </span>
));